/**
 * What Virelity sells, in one place. The homepage, /services and /about all
 * read from here so the positioning stays the same on every page.
 */
import { Bot, BookOpen, GraduationCap, Plug, ShieldCheck, Terminal, type LucideIcon } from "lucide-react";

export interface Offering {
  /** Anchor id on /services, e.g. /services#mcp */
  id: string;
  icon: LucideIcon;
  title: string;
  /** One or two plain sentences for cards. */
  text: string;
  /** What the client actually receives. Shown on /services. */
  deliverables: string[];
}

export const offerings: Offering[] = [
  {
    id: "mcp",
    icon: Plug,
    title: "Custom MCP servers",
    text: "We connect Claude, ChatGPT, Cursor and other AI tools to your CRM, database and internal systems, so the AI can look things up and take action instead of guessing.",
    deliverables: [
      "An MCP server for each system you want the AI to reach",
      "Read and write permissions set per tool",
      "Hosted on your infrastructure or ours, with the source code handed over",
    ],
  },
  {
    id: "skills",
    icon: BookOpen,
    title: "Skills & playbooks",
    text: "Your best people's process, written down as skills the AI works from: how you qualify a lead, write a report, review a contract or ship a release.",
    deliverables: [
      "Skills written from your real documents and working sessions",
      "Shared across the team from one repository",
      "Updated as your process changes",
    ],
  },
  {
    id: "agents",
    icon: Bot,
    title: "Agents & automation",
    text: "Agents that carry a job from start to finish: answer support tickets, chase invoices, prepare the weekly report, move data between the tools you already use.",
    deliverables: [
      "Agents built for one job each, measured against how it is done today",
      "Scheduled or triggered runs, with a log of every step",
      "A human approval step wherever the action cannot be undone",
    ],
  },
  {
    id: "coding-agents",
    icon: Terminal,
    title: "Coding-agent setup",
    text: "For engineering teams: Claude Code, Codex or Cursor rolled out properly, with project instructions, subagents, hooks and review loops tuned to your codebase.",
    deliverables: [
      "Project instructions and conventions the agent reads on every run",
      "Subagents, hooks and commands for your build, test and release flow",
      "A working session with your engineers on their own repository",
    ],
  },
  {
    id: "guardrails",
    icon: ShieldCheck,
    title: "Guardrails & evals",
    text: "You decide what the AI can read, what it can change and what needs a person to approve. We write the tests that show it works before anyone depends on it.",
    deliverables: [
      "Permission rules and approval gates for every connected system",
      "A test set built from your real cases, run before each change ships",
      "Logging so you can see what the AI did and why",
    ],
  },
  {
    id: "training",
    icon: GraduationCap,
    title: "Training & support",
    text: "Hands-on sessions so your team uses what we build, then ongoing tuning as models, tools and your business change.",
    deliverables: [
      "Live sessions on your own work, not slides",
      "Written guides for each skill and agent",
      "A support channel and regular tune-ups after launch",
    ],
  },
];

export interface Term {
  term: string;
  definition: string;
}

export const glossary: Term[] = [
  {
    term: "Harness",
    definition:
      "Everything around the AI model that lets it do a job: the tools it can use, the instructions it follows and the checks that keep it safe. The model is the engine. The harness is the rest of the car.",
  },
  {
    term: "MCP server",
    definition:
      "MCP is the Model Context Protocol, an open standard for connecting AI tools to other software. An MCP server is the connector: it lets the AI search your CRM or update a ticket, inside the permissions you set.",
  },
  {
    term: "Skill",
    definition:
      "A written playbook the AI loads when a task comes up. It tells the AI how your company does that task, step by step, so the work is done your way whoever asks.",
  },
  {
    term: "Agent",
    definition:
      "An AI that works through a task in several steps on its own: it plans, uses tools, checks the result and reports back, stopping for approval where you tell it to.",
  },
];

export interface Step {
  title: string;
  text: string;
}

export const steps: Step[] = [
  {
    title: "Map",
    text: "We sit with your team and list the work that eats their week. You get a short plan: which jobs AI should take first, which systems it needs to reach and what each one saves.",
  },
  {
    title: "Build",
    text: "We build the harness for the first job: the MCP servers, the skills, the agent and its guardrails. It runs next to the old way until the numbers say it is better.",
  },
  {
    title: "Roll out",
    text: "We train everyone who touches it, hand over the code and the guides, then move to the next job on the list.",
  },
];

export interface OpenSourceProject {
  name: string;
  text: string;
  url: string;
}

/** Public repositories from Virelity's founder. Descriptions follow each README. */
export const openSource: OpenSourceProject[] = [
  {
    name: "MantisHack",
    text: "A security harness: an agent that hunts for vulnerabilities in a codebase and keeps only the findings it can validate.",
    url: "https://github.com/deonmenezes/mantishack",
  },
  {
    name: "edit-ai",
    text: "A video-editing harness. You describe the edit, an agent makes it through 16 MCP tools and stops for approval before anything destructive.",
    url: "https://github.com/deonmenezes/edit-ai",
  },
  {
    name: "mobile-app-skill",
    text: "A Claude Code skill pack that takes a mobile app from idea to App Store and Google Play submission.",
    url: "https://github.com/deonmenezes/mobile-app-skill",
  },
  {
    name: "loop-engineering-harness",
    text: "Describe the agent team you want and get a self-contained multi-agent harness back, with its own app and terminal interface.",
    url: "https://github.com/deonmenezes/loop-engineering-harness",
  },
];

export interface Faq {
  q: string;
  a: string;
}

export const faqs: Faq[] = [
  {
    q: "We already pay for ChatGPT or Claude. Why do we need this?",
    a: "Out of the box those tools know very little about your business: what you paste in and the few apps you connect by hand. A harness connects them to your systems and your process, so they can do the work inside your business.",
  },
  {
    q: "Which AI tools do you work with?",
    a: "Claude, ChatGPT, Cursor, Claude Code, Codex and anything else that speaks MCP. MCP is an open standard, so the servers we build are not tied to one vendor.",
  },
  {
    q: "Do we need our own engineers?",
    a: "No. We build, host and maintain the setup if you want us to. If you have engineers, we work in your repositories and hand everything over.",
  },
  {
    q: "What happens to our data?",
    a: "The AI reaches only the systems you approve, with the permissions you set. Anything that changes data can be made to wait for a person. We can run the servers on your own infrastructure.",
  },
  {
    q: "How do we start?",
    a: "Book a free call. We look at how your team works today and tell you the first job worth handing to AI. If there isn't one yet, we'll say so.",
  },
];
